// Billing (Stripe subscriptions) types for the user-facing UI. Mirrors
// docs/plan/stripe-subscriptions.md, Appendix A ("Public / user").

export type BillingConfig = {
  enabled: boolean;
  signupEnabled?: boolean;
  publishableKey?: string | null;
  currency?: string;
};

export type BillingInterval = "month" | (string & {});

export type BillingPrice = {
  /** Amount in minor units (cents). */
  amountMinor: number;
  currency: string;
  interval: BillingInterval;
};

export type BillingPlan = {
  slug: string;
  name: string;
  description: string;
  /** null = unlimited. */
  maxNamespaces: number | null;
  /** null = unlimited. */
  maxSeats: number | null;
  price: BillingPrice;
};

export type BillingPlansResponse = {
  plans: BillingPlan[];
};

export type SubscriptionState =
  | "unlimited"
  | "none"
  | "pending"
  | "active"
  | "past_due"
  | "locked"
  | "cancelled";

export type SubscriptionSource = "stripe" | "manual";

export type SubscriptionLimits = {
  /** null = unlimited. */
  maxNamespaces: number | null;
  /** null = unlimited. */
  maxSeats: number | null;
};

export type SubscriptionUsage = {
  namespaces: number;
  /** Active users + pending invites. */
  seats: number;
};

export type SubscriptionView = {
  state: SubscriptionState;
  source?: SubscriptionSource;
  plan?: { slug: string; name: string };
  price?: BillingPrice;
  currentPeriodEnd?: string;
  graceUntil?: string;
  contractExpiresAt?: string;
  limits: SubscriptionLimits;
  usage: SubscriptionUsage;
};

export type CheckoutRequest = {
  planSlug: string;
};

export type CheckoutResponse = {
  checkoutUrl: string;
};

export type PortalResponse = {
  portalUrl: string;
};

export type ChangePlanOverflow = {
  namespaces: number;
  seats: number;
};

export type ChangePlanPreview = {
  overflow: ChangePlanOverflow;
  candidates: {
    namespaces: Array<{ id: string; name: string; slug: string }>;
    users: Array<{ id: string; email: string; displayName: string }>;
  };
};

export type ChangePlanRequest = {
  planSlug: string;
  disableNamespaceIds: string[];
  removeUserIds: string[];
};

/** Error codes the billing and limit endpoints return in `{ error }`. */
export const BILLING_ERROR_CODES = {
  namespaceLimitReached: "namespace_limit_reached",
  seatLimitReached: "seat_limit_reached",
  subscriptionLocked: "subscription_locked",
  subscriptionExists: "subscription_exists",
  downgradeRequiresSelection: "downgrade_requires_selection",
} as const;

export type BillingErrorCode = (typeof BILLING_ERROR_CODES)[keyof typeof BILLING_ERROR_CODES];

/** The org:owner scope: full authority over the org, including billing. */
export const ORG_OWNER_SCOPE = "org:owner";
